// SmartCartDemo — public-facing demo for the Health Fiduciary Agent.
//
// Three sections:
//   1. Input — chip editor for userHistory + textarea for userCommand
//   2. CTA — "Generate Smart Cart" button (triggers POST /api/build-smart-cart)
//   3. Result — Before/After split-screen comparison + total + push-to-Blinkit CTA
//
// All visual treatment follows the existing dark + saffron CartIQ language so
// it slots into the app without bespoke theming.

import React, { useCallback, useMemo, useState } from "react";
import { ArrowRight, Loader2, Sparkles, X, AlertCircle, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import LandingBottomNav from "@/components/LandingBottomNav";

const DEFAULT_HISTORY = ["Lays Chips", "Thums Up", "Haldiram Bhujia"];
const DEFAULT_COMMAND = "Order healthy weekend snacks under ₹500";

const formatINR = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Math.round(Number(n) || 0));

export default function SmartCartDemo() {
  const [history, setHistory] = useState(DEFAULT_HISTORY);
  const [chipDraft, setChipDraft] = useState("");
  const [command, setCommand] = useState(DEFAULT_COMMAND);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [retryIn, setRetryIn] = useState(0);
  const [result, setResult] = useState(null);

  // Push-to-Blinkit (save-planned-order) state
  const [pushing, setPushing] = useState(false);
  const [pushed, setPushed] = useState(false);
  const [pushError, setPushError] = useState(null);
  const [plannedOrderId, setPlannedOrderId] = useState(null);

  // ── Chip helpers ───────────────────────────────────────────────────
  const addChip = useCallback(
    (raw) => {
      const v = (raw || "").trim();
      if (!v) return;
      // Case-insensitive de-dupe
      setHistory((prev) =>
        prev.some((p) => p.toLowerCase() === v.toLowerCase())
          ? prev
          : [...prev, v.slice(0, 80)],
      );
      setChipDraft("");
    },
    [],
  );
  const removeChip = useCallback((idx) => {
    setHistory((prev) => prev.filter((_, i) => i !== idx));
  }, []);
  const onChipKeyDown = (e) => {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addChip(chipDraft);
    } else if (e.key === "Backspace" && chipDraft === "" && history.length) {
      // Quick UX: backspace on empty input removes last chip
      setHistory((prev) => prev.slice(0, -1));
    }
  };

  // ── Submit ─────────────────────────────────────────────────────────
  const onGenerate = useCallback(async () => {
    setLoading(true);
    setError(null);
    setRetryIn(0);
    setResult(null);
    setPushed(false);
    setPushError(null);
    setPlannedOrderId(null);
    try {
      const res = await api.post("/build-smart-cart", {
        userCommand: command.trim(),
        userHistory: history,
      });
      setResult(res?.data || null);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        const wait = (detail && detail.retryAfter) || 60;
        setRetryIn(wait);
        setError(
          (detail && detail.message) ||
            `You're going a bit fast. Try again in ${wait}s.`,
        );
      } else if (status === 422) {
        setError("Please enter a command (between 2 and 500 characters).");
      } else if (status === 502) {
        setError(
          (detail && detail.message) ||
            "The AI returned an unexpected response. Try rephrasing.",
        );
      } else {
        setError(e?.message || "Could not build cart. Try again in a moment.");
      }
    } finally {
      setLoading(false);
    }
  }, [command, history]);

  const canSubmit =
    !loading && command.trim().length >= 2 && command.trim().length <= 500;

  // Total bar derives from server response; sum-check shown subtly for trust.
  const sumOfPrices = useMemo(() => {
    if (!result?.final_cart) return 0;
    return result.final_cart.reduce(
      (acc, it) => acc + (Number(it.price) || 0),
      0,
    );
  }, [result]);

  // ── Push to Blinkit / save-planned-order ────────────────────────
  const onPush = useCallback(async () => {
    if (!result || pushed || pushing) return;
    setPushing(true);
    setPushError(null);
    try {
      const payload = {
        final_cart: result.final_cart || [],
        total_estimated_price:
          Number(result.total_estimated_price) || sumOfPrices,
        original_items: result.original_items || [],
        command: command.trim() || null,
      };
      const res = await api.post("/save-planned-order", payload);
      const savedId = res?.data?.id;
      if (!savedId) {
        throw new Error("Server did not return a planned order id.");
      }
      setPlannedOrderId(savedId);
      setPushed(true);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 429) {
        const wait = (detail && detail.retryAfter) || 60;
        setPushError(
          (detail && detail.message) ||
            `Too many requests. Try again in ${wait}s.`,
        );
      } else if (status === 422) {
        setPushError("Nothing to save — generate a cart first.");
      } else {
        setPushError(
          (detail && detail.message) ||
            e?.message ||
            "Could not save your planned order. Please try again.",
        );
      }
    } finally {
      setPushing(false);
    }
  }, [result, pushed, pushing, sumOfPrices, command]);

  return (
    <div className="cartiq-wellness min-h-screen bg-[#F8FAFC] text-[#334155]">
      <div className="max-w-6xl mx-auto px-4 md:px-6 pt-10 md:pt-16 pb-32">
        {/* ── Hero ─────────────────────────────────────────────────── */}
        <header className="mb-10" data-testid="smart-cart-hero">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full sc-mint-tag border text-[11px] uppercase tracking-[0.22em] font-bold">
            <Sparkles className="w-3 h-3" />
            CartIQ AI · Health Fiduciary
          </div>
          <h1 className="font-display uppercase mt-6 text-3xl md:text-5xl leading-[1.05] text-[#0F172A]">
            Build a smarter cart in <span className="text-[#3EB489]">10 seconds</span>.
          </h1>
          <p className="mt-4 text-sm md:text-base text-[#64748B] max-w-2xl">
            Drop in your usual order, tell us what you actually want. Our Health
            Fiduciary Agent swaps junk for Indian quick-commerce alternatives —
            roasted makhana, kombucha, millet snacks — and stays under your
            budget.
          </p>
        </header>

        {/* ── Inputs ──────────────────────────────────────────────── */}
        <section
          className="cartiq-card mb-6"
          data-testid="smart-cart-input-card"
        >
          {/* History chips */}
          <div className="mb-5">
            <label className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
              Your usual cart
            </label>
            <p className="text-xs text-[#666] mt-0.5 mb-2">
              Press Enter or comma to add. Backspace on empty input removes the last chip.
            </p>
            <div
              className="flex flex-wrap gap-2 p-3 border border-[#2A2A35] rounded bg-[#0F0F1A] focus-within:border-[#FF6B00] transition-colors"
              data-testid="history-chips"
            >
              {history.map((chip, idx) => (
                <span
                  key={`${chip}-${idx}`}
                  data-testid={`chip-${idx}`}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-[#1E1E2E] border border-[#2A2A35] text-sm"
                >
                  {chip}
                  <button
                    type="button"
                    onClick={() => removeChip(idx)}
                    className="text-[#888] hover:text-[#FF6B00]"
                    aria-label={`Remove ${chip}`}
                    data-testid={`chip-remove-${idx}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                type="text"
                value={chipDraft}
                onChange={(e) => setChipDraft(e.target.value)}
                onKeyDown={onChipKeyDown}
                onBlur={() => chipDraft && addChip(chipDraft)}
                placeholder={
                  history.length ? "Add another item…" : "Type an item and press Enter"
                }
                className="flex-1 min-w-[140px] bg-transparent outline-none text-sm placeholder-[#555]"
                data-testid="chip-input"
              />
            </div>
          </div>

          {/* Command */}
          <div className="mb-2">
            <label className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold">
              What do you want today?
            </label>
            <textarea
              value={command}
              onChange={(e) => setCommand(e.target.value)}
              rows={2}
              maxLength={500}
              className="mt-2 w-full p-3 bg-[#0F0F1A] border border-[#2A2A35] rounded text-sm placeholder-[#555] focus:border-[#FF6B00] outline-none resize-y"
              placeholder='e.g., "Order healthy weekend snacks under ₹500"'
              data-testid="command-input"
            />
            <div className="text-[10px] text-[#555] uppercase tracking-widest mt-1">
              {command.length} / 500
            </div>
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={onGenerate}
            disabled={!canSubmit}
            className="btn-saffron w-full md:w-auto inline-flex items-center justify-center gap-2 px-7 py-3.5 mt-3 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="generate-smart-cart-btn"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Building your cart…
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4" />
                Generate Smart Cart
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          {error ? (
            <div
              className="mt-4 flex items-start gap-2 p-3 border border-[#E24B4A] rounded bg-[#3B1212]/40"
              data-testid="smart-cart-error"
            >
              <AlertCircle className="w-4 h-4 text-[#E24B4A] mt-0.5 shrink-0" />
              <div className="text-sm text-[#F0EDE8]">
                {error}
                {retryIn > 0 ? (
                  <span className="block mt-1 text-[11px] uppercase tracking-widest text-[#888]">
                    Retry in ~{retryIn}s
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
        </section>

        {/* ── Result — Before / After split-screen ─────────────────── */}
        {result ? (
          <SmartCartResultView
            result={result}
            sumOfPrices={sumOfPrices}
            pushed={pushed}
            pushing={pushing}
            pushError={pushError}
            plannedOrderId={plannedOrderId}
            onPush={onPush}
          />
        ) : null}
      </div>

      <LandingBottomNav />
    </div>
  );
}

// ── Result view ──────────────────────────────────────────────────────────

function SmartCartResultView({
  result,
  sumOfPrices,
  pushed,
  pushing,
  pushError,
  plannedOrderId,
  onPush,
}) {
  const originalItems = result.original_items || [];
  const finalCart = result.final_cart || [];
  const total = Number(result.total_estimated_price) || sumOfPrices;
  const isEmpty = finalCart.length === 0;

  return (
    <section data-testid="smart-cart-result">
      {/* Divider eyebrow */}
      <div className="flex items-center gap-3 mb-5">
        <div className="h-px bg-[#2A2A35] flex-1" />
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#1D9E75] font-bold">
          Your CartIQ Smart Cart
        </div>
        <div className="h-px bg-[#2A2A35] flex-1" />
      </div>

      {isEmpty ? (
        <div className="cartiq-card text-center py-10">
          <Sparkles className="w-6 h-6 mx-auto text-[#666] mb-2" />
          <div className="text-sm text-[#888]">
            We couldn&apos;t build a cart from that. Try a clearer command (e.g.,
            adding a budget or a meal time).
          </div>
        </div>
      ) : (
        <>
          {/* Split-screen Before / After */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
            {/* LEFT — Before */}
            <div
              className="bg-white rounded-xl border border-[#E2E8F0] p-6 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
              data-testid="smart-cart-before"
            >
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full sc-coral-tag border text-[10px] uppercase tracking-[0.22em] font-bold mb-3 text-[#E11D48]">
                Swapped out
              </div>
              <h2 className="font-display uppercase text-xl md:text-2xl mb-4 text-[#0F172A]">
                Before
              </h2>
              {originalItems.length === 0 ? (
                <div className="text-sm text-[#94A3B8] italic">No prior history provided.</div>
              ) : (
                <ul className="space-y-2.5">
                  {originalItems.map((it, idx) => (
                    <li
                      key={`${it}-${idx}`}
                      className="flex items-center gap-3 text-sm"
                      data-testid={`before-item-${idx}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FB7185] shrink-0" />
                      <span className="sc-before-strike">{it}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-5 pt-4 border-t border-[#E2E8F0] text-[11px] text-[#94A3B8] uppercase tracking-widest font-semibold">
                Ultra-processed · high sugar · high sodium
              </div>
            </div>

            {/* RIGHT — After */}
            <div
              className="sc-mint-card rounded-xl p-6"
              data-testid="smart-cart-after"
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full sc-mint-tag border text-[10px] uppercase tracking-[0.22em] font-bold text-[#2D8C6E]">
                  <ShieldCheck className="w-3 h-3" />
                  Health-curated
                </div>
              </div>
              <h2 className="font-display uppercase text-xl md:text-2xl mb-4 text-[#0F172A]">
                After
              </h2>
              <ul className="space-y-3">
                {finalCart.map((it, idx) => (
                  <li
                    key={`${it.item}-${idx}`}
                    className="bg-[#F8FAFC] border border-[#E2E8F0] rounded-lg p-3 md:p-4"
                    data-testid={`after-item-${idx}`}
                  >
                    <div className="flex items-baseline justify-between gap-3 mb-2">
                      <div className="text-sm md:text-base font-bold text-[#0F172A]">
                        {it.item}
                      </div>
                      <div
                        className="font-mono-num font-extrabold text-[#2D8C6E] text-base md:text-lg shrink-0"
                        data-testid={`after-item-price-${idx}`}
                      >
                        {formatINR(it.price)}
                      </div>
                    </div>
                    {it.health_reasoning ? (
                      <div
                        className="text-[12px] md:text-[13px] leading-relaxed text-[#334155] bg-white border-l-2 border-[#3EB489] pl-3 py-1.5 rounded-r"
                        data-testid={`after-item-reasoning-${idx}`}
                      >
                        {it.health_reasoning}
                      </div>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Bottom summary + push-to-blinkit */}
          <div
            className="mt-5 md:mt-6 bg-white rounded-xl border border-[#E2E8F0] p-6 flex flex-col md:flex-row md:items-center gap-4 md:gap-6 shadow-[0_1px_3px_rgba(15,23,42,0.04)]"
            data-testid="smart-cart-summary"
          >
            <div className="flex-1">
              <div className="text-[11px] uppercase tracking-[0.22em] text-[#64748B] font-bold mb-1">
                Total Estimated Price
              </div>
              <div className="flex items-baseline gap-3">
                <div
                  className="font-display text-4xl md:text-5xl text-[#0F172A]"
                  data-testid="smart-cart-total"
                >
                  {formatINR(total)}
                </div>
                <div className="text-xs text-[#94A3B8] uppercase tracking-widest font-semibold">
                  {finalCart.length} items
                </div>
              </div>
              {/* Trust micro-row */}
              <div className="mt-2 text-xs text-[#64748B] flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="inline-flex items-center gap-1">
                  <ShieldCheck className="w-3 h-3 text-[#3EB489]" />
                  Health-curated by CartIQ AI
                </span>
                <span className="text-[#CBD5E1]">·</span>
                <span>Prices indicative · live on Blinkit / Zepto / Swiggy</span>
              </div>
            </div>

            <div className="flex flex-col items-start md:items-end gap-2">
              <button
                type="button"
                disabled={pushed || pushing}
                onClick={onPush}
                data-testid="push-to-blinkit-btn"
                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-lg font-bold uppercase tracking-widest text-sm transition-all disabled:cursor-not-allowed"
                style={{
                  background: pushed ? "rgba(62,180,137,0.12)" : "#3EB489",
                  color: pushed ? "#2D8C6E" : "#FFFFFF",
                  border: pushed ? "1px solid rgba(62,180,137,0.3)" : "1px solid #3EB489",
                  boxShadow: pushed ? "none" : "0 4px 12px rgba(62,180,137,0.25)",
                  opacity: pushing ? 0.7 : 1,
                }}
              >
                {pushing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving plan…
                  </>
                ) : pushed ? (
                  <>
                    <ShieldCheck className="w-4 h-4" />
                    Approved · Pushed to Blinkit
                  </>
                ) : (
                  <>
                    Approve & Push to Blinkit
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
              {pushed && plannedOrderId ? (
                <div
                  className="text-[10px] uppercase tracking-[0.22em] text-[#2D8C6E] font-bold"
                  data-testid="planned-order-confirmation"
                >
                  Planned order saved · #{plannedOrderId.slice(0, 8)}
                </div>
              ) : null}
              {pushError ? (
                <div
                  className="text-xs text-[#FB7185] max-w-[280px] text-left md:text-right"
                  data-testid="push-error"
                >
                  {pushError}
                </div>
              ) : null}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
